// Treatment Search Bar Component for Hero Section
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowRight, TrendingUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive treatment taxonomy
const TREATMENT_TAXONOMY = {
  "Cardiology": ["Heart Bypass Surgery", "Angioplasty", "Valve Replacement", "Pacemaker Implant", "Heart Transplant", "Coronary Artery Bypass", "Cardiac Catheterization", "Atrial Fibrillation Treatment"],
  "Orthopedics": ["Knee Replacement", "Hip Replacement", "Spine Surgery", "Arthroscopy", "ACL Reconstruction", "Shoulder Surgery", "Joint Fusion", "Bone Fracture Treatment", "Scoliosis Correction"],
  "Oncology": ["Chemotherapy", "Radiation Therapy", "Immunotherapy", "Tumor Removal", "Breast Cancer Treatment", "Lung Cancer Treatment", "Prostate Cancer", "Blood Cancer Treatment", "Bone Marrow Transplant"],
  "Neurology": ["Brain Tumor Surgery", "Spine Fusion", "Deep Brain Stimulation", "Epilepsy Surgery", "Parkinson's Treatment", "Stroke Treatment", "Aneurysm Repair", "Spinal Cord Injury"],
  "Transplant": ["Kidney Transplant", "Liver Transplant", "Heart Transplant", "Lung Transplant", "Bone Marrow Transplant", "Cornea Transplant", "Pancreas Transplant"],
  "Dental": ["Dental Implants", "Root Canal", "Teeth Whitening", "Veneers", "Crowns & Bridges", "Orthodontics", "Wisdom Tooth Extraction", "Full Mouth Rehabilitation", "Gum Treatment"],
  "Cosmetic": ["Rhinoplasty", "Liposuction", "Facelift", "Breast Augmentation", "Tummy Tuck", "Hair Transplant", "Botox", "Dermal Fillers", "Body Contouring"],
  "Eye Care": ["LASIK", "Cataract Surgery", "Glaucoma Treatment", "Retinal Surgery", "Corneal Transplant", "Vitrectomy", "Strabismus Surgery"],
  "Fertility": ["IVF", "IUI", "ICSI", "Egg Freezing", "Surrogacy", "Male Infertility Treatment", "PCOS Treatment", "Fertility Preservation"],
  "Bariatric": ["Gastric Bypass", "Sleeve Gastrectomy", "Gastric Banding", "Mini Gastric Bypass", "Revision Surgery"],
  "Wellness": ["Ayurveda", "Panchakarma", "Yoga Retreat", "Meditation Program", "Detox Treatment", "Naturopathy", "Wellness Checkup"],
  "General": ["Health Checkup", "Second Opinion", "Diagnostic Services", "Emergency Care", "Post-Surgery Rehabilitation"]
};

export default function TreatmentSearchBar({ variant = "hero" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [popularSearches] = useState(["Knee Replacement", "Dental Implants", "Heart Bypass", "IVF", "Hair Transplant"]);
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/treatments`).then(r => setTreatments(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchQuery = query.toLowerCase();
    const matchedResults = [];

    // Search in taxonomy
    Object.entries(TREATMENT_TAXONOMY).forEach(([category, procedures]) => {
      procedures.forEach(procedure => {
        if (procedure.toLowerCase().includes(searchQuery)) {
          matchedResults.push({ type: "procedure", name: procedure, category });
        }
      });
      if (category.toLowerCase().includes(searchQuery)) {
        matchedResults.push({ type: "category", name: category, count: procedures.length });
      }
    });

    // Search in database treatments
    treatments.forEach(t => {
      if (t.name.toLowerCase().includes(searchQuery) && !matchedResults.find(r => r.name === t.name)) {
        matchedResults.push({ type: "treatment", name: t.name, price: t.price_range, id: t.id });
      }
    });

    setResults(matchedResults.slice(0, 8));
  }, [query, treatments]);

  const handleSelect = (result) => {
    if (result.type === "category") {
      navigate(`/treatments?category=${result.name}`);
    } else {
      navigate(`/treatments?search=${result.name}`);
    }
    setQuery("");
    setIsOpen(false);
  };

  const isHero = variant === "hero";

  return (
    <div ref={containerRef} className={`relative ${isHero ? "w-full max-w-2xl" : ""}`} data-testid="treatment-search">
      <div className={`relative ${isHero ? "bg-white rounded-full shadow-xl border border-slate-200 hover:shadow-2xl transition-shadow" : ""}`}>
        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 ${isHero ? "h-5 w-5" : "h-4 w-4"} text-slate-400`} />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search treatments, procedures, surgeries..."
          className={`${isHero ? "pl-12 pr-32 py-8 text-base rounded-full border-0 focus-visible:ring-0 focus-visible:ring-offset-0" : "pl-10 pr-4 py-2 text-sm rounded-lg"}`}
          data-testid="treatment-search-input"
        />
        {isHero && (
          <button
            onClick={() => query && navigate(`/treatments?search=${query}`)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-colors flex items-center gap-2"
            data-testid="treatment-search-btn"
          >
            Search <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (query || isHero) && (
        <div className={`absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 ${results.length === 0 && !query ? "p-4" : ""}`} data-testid="search-results">
          {results.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              {results.map((result, i) => (
                <button
                  key={i}
                  onClick={() => handleSelect(result)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-orange-50 transition-colors text-left border-b border-slate-50 last:border-0"
                  data-testid={`search-result-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${result.type === "category" ? "bg-orange-100" : "bg-slate-100"}`}>
                      <Search className={`h-4 w-4 ${result.type === "category" ? "text-orange-600" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{result.name}</p>
                      <p className="text-xs text-slate-500">
                        {result.type === "category" ? `${result.count} procedures` : result.category || result.price || "View details"}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </div>
          ) : query ? (
            <div className="p-6 text-center">
              <p className="text-slate-500 text-sm">No results found for "{query}"</p>
              <p className="text-xs text-slate-400 mt-1">Try searching for a different treatment or procedure</p>
            </div>
          ) : (
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider px-4 pt-4 pb-2 flex items-center gap-2">
                <TrendingUp className="h-3 w-3" /> Popular Searches
              </p>
              <div className="flex flex-wrap gap-2 px-4 pb-4">
                {popularSearches.map((s, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="cursor-pointer hover:bg-orange-50 hover:border-orange-300 transition-colors"
                    onClick={() => { setQuery(s); setIsOpen(true); }}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
